import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CASEBUILDER_BASE } from '../../../../../constants/setupRoadmapSteps';
import { getNaccIdFromDetails } from '../../../../../helpers/naccAlias';
import HelpPopover from '../../../../../components/blocks/HelpPopover';
import { useCaseList } from '../../shared/hooks/useCaseList';
import { useCaseCompleteness } from '../../shared/hooks/useCaseCompleteness';
import CaseStatusChip from '../components/CaseStatusChip';
import ConfirmDialog from '../components/ConfirmDialog';
import styles from '../CaseBuilderEnhanced.module.css';

export default function CaseWorkspaceHeader({
    doc,
    caseId,
    workspace,
}) {
    const navigate = useNavigate();
    const { cases, caseNav } = useCaseList(caseId);
    const completeness = useCaseCompleteness(doc);
    const [switcherQuery, setSwitcherQuery] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [saveTimer, setSaveTimer] = useState(null);

    const naccId = getNaccIdFromDetails(doc?.details);
    const protocolName = workspace.selectedProtocol?.name;
    const quarantined = !!doc?.quarantined;

    const switcherMatches = useMemo(() => {
        const q = switcherQuery.trim().toLowerCase();
        if (!q) return [];
        return cases.filter((c) => {
            const hay = [c.networkIdentifier, c.localIdentifier, getNaccIdFromDetails(c.details)]
                .filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        }).slice(0, 8);
    }, [cases, switcherQuery]);

    const debouncedSaveLocalId = useCallback((value) => {
        workspace.setLocalIdentifierDraft(value);
        if (saveTimer) clearTimeout(saveTimer);
        const timer = setTimeout(() => {
            if (value.trim() && value.trim() !== (doc?.localIdentifier ?? '')) {
                workspace.handleUpdateCaseName(value);
            }
        }, 800);
        setSaveTimer(timer);
    }, [workspace, doc?.localIdentifier, saveTimer]);

    const goToCase = (networkId) => {
        if (networkId) {
            navigate(`${CASEBUILDER_BASE}/${networkId}?step=overview`);
        }
    };

    return (
        <header className={styles.workspaceHeader}>
            <div className={styles.headerPanel}>
            <div className={styles.headerTop}>
                <div className={styles.headerIdentity}>
                    <div className={styles.titleCluster}>
                        <div className={styles.networkId}>{caseId}</div>
                        <div className={styles.localIdInline}>
                            <input
                                id="case-local-id"
                                type="text"
                                className={styles.localIdInput}
                                value={workspace.localIdentifierDraft}
                                onChange={(e) => debouncedSaveLocalId(e.target.value)}
                                placeholder="Local ID"
                                aria-label="Local case ID"
                                size={Math.max(
                                    8,
                                    (workspace.localIdentifierDraft || 'Local ID').length + 1,
                                )}
                            />
                            <HelpPopover label="About local ID" title="Local ID">
                                Internal lab identifier for this case. Visible in your workspace and
                                case list; it is not shown as the published network case ID.
                            </HelpPopover>
                        </div>
                    </div>

                    <div className={styles.headerMetaRow}>
                        <CaseStatusChip status={completeness.status} />
                        {naccId && (
                            <span className={`${styles.badge} ${styles.badgeDraft}`} title="NACC ID">
                                NACC: {naccId}
                            </span>
                        )}
                        <span className={styles.metaDot} aria-hidden="true">·</span>
                        <span className={styles.protocolMeta}>
                            {protocolName || 'No protocol'}
                        </span>
                    </div>

                    <div className={styles.quarantineToggleRow}>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={quarantined}
                            aria-label={quarantined ? 'Case quarantine on' : 'Case quarantine off'}
                            className={`${styles.quarantineSwitch} ${quarantined ? styles.quarantineSwitchOn : ''}`}
                            disabled={workspace.quarantinePending}
                            onClick={() => workspace.handleQuarantinedChange(!quarantined)}
                        >
                            <span className={styles.quarantineSwitchKnob} />
                        </button>
                        <div className={styles.formLabelRow}>
                            <span className={styles.quarantineToggleLabel}>Case quarantine</span>
                            <HelpPopover label="About case quarantine" title="Case quarantine">
                                While on, released slides stay off the network until you turn quarantine
                                off. Individual slides still need to be released separately.
                            </HelpPopover>
                        </div>
                    </div>
                </div>

                <div className={styles.headerActions}>
                    <div className={styles.caseSwitcher}>
                        <input
                            type="search"
                            className={styles.caseSwitcherInput}
                            placeholder="Switch case…"
                            value={switcherQuery}
                            onChange={(e) => setSwitcherQuery(e.target.value)}
                            aria-label="Search cases to switch"
                        />
                        <button
                            type="button"
                            className={styles.caseNavButton}
                            disabled={!caseNav.prev}
                            title={caseNav.prev ? `Previous: ${caseNav.prev.networkIdentifier}` : undefined}
                            onClick={() => goToCase(caseNav.prev?.networkIdentifier)}
                        >
                            ‹
                        </button>
                        <span className={styles.slideCardMeta}>
                            {caseNav.index >= 0 ? caseNav.index + 1 : '—'} / {caseNav.total}
                        </span>
                        <button
                            type="button"
                            className={styles.caseNavButton}
                            disabled={!caseNav.next}
                            title={caseNav.next ? `Next: ${caseNav.next.networkIdentifier}` : undefined}
                            onClick={() => goToCase(caseNav.next?.networkIdentifier)}
                        >
                            ›
                        </button>
                    </div>
                    <Link to={CASEBUILDER_BASE} className={styles.linkButton}>
                        Library
                    </Link>
                    <button
                        type="button"
                        className={styles.buttonDanger}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={workspace.deletePending}
                    >
                        Delete
                    </button>
                </div>
            </div>

            {switcherMatches.length > 0 && (
                <div className={styles.panel} style={{ marginTop: '0.5rem', padding: '0.5rem' }}>
                    {switcherMatches.map((c) => (
                        <button
                            key={c.networkIdentifier}
                            type="button"
                            className={styles.linkButton}
                            style={{ display: 'block', marginBottom: '0.25rem' }}
                            onClick={() => goToCase(c.networkIdentifier)}
                        >
                            {c.networkIdentifier}
                            {c.localIdentifier ? ` (${c.localIdentifier})` : ''}
                        </button>
                    ))}
                </div>
            )}
            </div>

            <ConfirmDialog
                open={showDeleteConfirm}
                title="Delete case?"
                message="This permanently deletes the case and cannot be undone."
                confirmLabel="Delete"
                variant="danger"
                onConfirm={() => {
                    setShowDeleteConfirm(false);
                    workspace.handleDeleteCase();
                }}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </header>
    );
}
